<!--suppress JSUnresolvedFunction, CssUnusedSymbol, JSUnresolvedVariable -->
<template>
  <page-template page-id="registration" ref="super">

    <document-title :title="$tb('pages.registration.documentTitle')"></document-title>

    <template v-slot:before v-if="isTitlebarActive">
      <titlebar :text="$tb('pages.registration.header', {0: $store.state.appName})"></titlebar>
    </template>

    <div class="ui column">

      <brand-header :text="$t('pages.registration.title')"
                    :has-extension="hasHeaderExtension"
                    :has-logo="hasBrandLogo">
      </brand-header>

      <messages id="Notifications" ref="postman" error-element-id="server-errors" aria-role="none"></messages>

      <h4 class="ui horizontal divider header">
        {{ $t('pages.registration.section.username') }}
      </h4>

      <p>{{ $t('pages.registration.usernameInfo') }} </p>

      <validated-field :show-errors-on="showErrors" :has-any-errors="$v.username.$invalid"
                       id="username-errors"
                       :validation-rules="[
                             {value: $v.username.required, msg: usernameEmptyError},
                             {value: $v.username.validEmail, showOn: $v.username.required && (!useMobile || $v.username.hasOwnProperty('email')), msg: $t('common.validation.username.emailFormat')},
                             {value: $v.username.validMobile, showOn: $v.username.required && (useMobile && !$v.username.hasOwnProperty('email')), msg: $t('common.validation.username.mobileFormat')}]">

        <labeled-input-field v-if="passwordAndEmailEnforced || !useMobile" id="username" v-model="$v.username.$model"
                             autocomplete="username"
                             ref="username"
                             type="email"
                             :label="$t('common.label.email')"
                             :mandatory="true"
                             aria-error-element-id="username-errors server-errors"
                             @enter-pressed="onContinue"
                             :invalid="$v.username.$invalid && showErrors">
        </labeled-input-field>

        <labeled-input-field v-else id="username" v-model="$v.username.$model"
                             autocomplete="username"
                             ref="username"
                             type="tel"
                             :label="$t('common.label.mobile')"
                             :mandatory="true"
                             aria-error-element-id="username-errors server-errors"
                             @enter-pressed="onContinue"
                             @input="onMobileInput"
                             :invalid="$v.username.$invalid && showErrors">
        </labeled-input-field>
      </validated-field>

      <div v-if="!passwordAndEmailEnforced" class="field left aligned">
        <div id="usernameMethod" class="ui toggle checkbox">
          <input tabindex="0" id="mobileSwitch" type="checkbox" :checked="useMobile" @click="switchType"/>
          <label for="mobileSwitch"
                 v-text="useMobile ? $t('pages.registration.useMobileChecked') : $t('pages.registration.useMobile')"></label>
        </div>
      </div>

      <h4 class="ui horizontal divider header">
        {{ $t('pages.registration.section.personal') }}
      </h4>

      <!--Honey pot trap -->
      <input type="checkbox" name="registrationCheck" v-model="humanCheck" v-if="isBotCheckActive"
             tabindex="-1" autocomplete="off">
      <validated-field :show-errors-on="showErrors" :has-any-errors="!$v.mobile.validMobile"
                        id="mobile-errors"
                        :validation-rules="[
                              {value: $v.mobile.required, showOn: !mobile, msg: $t('common.validation.username.mobileEmpty')},
                              {value: $v.mobile.validMobile, showOn: mobile && !$v.mobile.validMobile, msg: $t('common.validation.username.mobileFormat')}]">
        <labeled-input-field id="mobile" v-model="$v.mobile.$model"
                          autocomplete="mobile"
                          ref="mobile"
                          type="tel"
                          :label="$t('common.label.mobile')"
                          :mandatory="true"
                          aria-error-element-id="mobile-errors server-errors"
                          @enter-pressed="onContinue"
                          @input="onMobileInput"
                          :invalid="!$v.mobile.validMobile && showErrors">
        </labeled-input-field>
      </validated-field>
      <validated-field :show-errors-on="showErrors" :has-any-errors="$v.firstname.$invalid"
                       id="firstname-errors"
                       :validation-rules="[
                            {value: $v.firstname.required, showOn: !firstname, msg: $t('common.validation.field.empty')},
                            {value: $v.firstname.validName, showOn: firstname && containsInvalidCharacter(firstname), msg: $t('common.validation.field.forbiddenChars')},
                            {value: $v.firstname.maxLength, showOn: firstname && !containsInvalidCharacter(firstname) && firstname.length > nameLength, msg: $t('common.validation.field.maxLength', { 0: nameLength })}]">
        <labeled-input-field id="firstname" v-model="$v.firstname.$model"
                             autocomplete="given-name"
                             :label="$t('common.label.firstname')"
                             :mandatory="true"
                             aria-error-element-id="firstname-errors"
                             @enter-pressed.prevent="onContinue"
                             :invalid="$v.firstname.$invalid && showErrors"></labeled-input-field>
      </validated-field>

      <validated-field
          id="lastname-errors"
          :show-errors-on="showErrors" :has-any-errors="$v.lastname.$invalid"
          :validation-rules="[
                            {value: $v.lastname.required, showOn: !lastname, msg: $t('common.validation.field.empty')},
                            {value: $v.lastname.validName, showOn: lastname && containsInvalidCharacter(lastname), msg: $t('common.validation.field.forbiddenChars')},
                            {value: $v.lastname.maxLength, showOn: lastname && !containsInvalidCharacter(lastname) && lastname.length > nameLength, msg: $t('common.validation.field.maxLength', { 0: nameLength })}]">
        <labeled-input-field id="lastname" v-model="$v.lastname.$model"
                             autocomplete="family-name"
                             :label="$t('common.label.lastname')"
                             :mandatory="true"
                             aria-error-element-id="lastname-errors"
                             @enter-pressed.prevent="onContinue"
                             :invalid="$v.lastname.$invalid && showErrors"></labeled-input-field>
      </validated-field>

      <validated-field
          id="language-errors"
          :show-errors-on="showErrors" :has-any-errors="$v.language.$invalid"
          :validation-rules="[{value: $v.language.required, msg: $t('common.validation.field.empty')}]">
        <dropdown-select v-if="isMobile()" id="language" name="language" v-model="$v.language.$model"
                         classes="ui fluid dropdown selection"
                         :placeholder="$t('common.label.language')"
                         :options="languageOptions"
                         :mandatory="true"
                         :key="this.langConfigLoaded"
                         @change="updateLanguage(); fetchLegalTexts();"
                         :invalid="$v.language.$invalid && showErrors"></dropdown-select>
        <dropdown-mk2 v-else id="language" v-model="$v.language.$model" ref="languageDropdown"
                      :placeholder="$t('common.label.language')"
                      :options="languageOptions"
                      :mandatory="true"
                      :key="this.langConfigLoaded"
                      @change="updateLanguage(); fetchLegalTexts();"
                      aria-error-element-id="language-errors"
                      :invalid="$v.language.$invalid && showErrors"></dropdown-mk2>
      </validated-field>

      <validated-field
          id="country-errors"
          :show-errors-on="showErrors" :has-any-errors="$v.country.$invalid"
          :validation-rules="[{value: $v.country.required, msg: $t('common.validation.field.empty')}]">
        <dropdown-select v-if="isMobile()" id="country" name="country" v-model="$v.country.$model"
                         classes="ui fluid dropdown selection"
                         :placeholder="$t('common.label.country')"
                         :mandatory="true"

                         @change="updateCountry"
                         :options="countryOptions"
                         :invalid="$v.country.$invalid && showErrors"></dropdown-select>
        <dropdown-mk2 v-else id="country" v-model="$v.country.$model"
                      :placeholder="$t('common.label.country')"
                      :options="countryOptions"
                      :mandatory="true"

                      @change="updateCountry"
                      aria-error-element-id="country-errors"
                      :invalid="$v.country.$invalid && showErrors"></dropdown-mk2>
      </validated-field>

      <validated-field
          id="password-errors"
          :show-errors-on="showErrors" :has-any-errors="$v.password.$invalid"
          :validation-rules="[{value: $v.password.requiredIf, msg: $t('common.validation.field.empty')},
                                                 {value: $v.password.guidelines, msg: $t('common.validation.password.guidelines')},
                                                 {value: $v.password.noInvalidCharacters, showOn: $v.password.guidelines, msg: $t('common.validation.password.invalidCharacter')}]">

        <password-input-field id="password"
                              autocomplete="new-password"
                              :label="$t('common.label.password')"
                              @enter-pressed.prevent="onContinue"
                              v-model="$v.password.$model"
                              :mandatory="isPasswordMandatory()"
                              aria-error-element-id="aria-password-rules-summary password-errors aria-password-status"
                              :aria-label-text="$t('common.label.password')"
                              :invalid="$v.password.$invalid && showErrors"></password-input-field>

        <span id="aria-password-rules-summary" style="height:0; overflow:hidden; position:absolute;" v-if="!password">
                    {{ $t('components.password.aria.rules.summary') }}
        </span>
      </validated-field>
      <validated-field
        id="confirm-password-errors"
        v-if="passwordConfirmationEnabled"
        :show-errors-on="showErrors" :has-any-errors="$v.confirmPassword.$invalid"
        :validation-rules="[{value: $v.confirmPassword.requiredIf, msg: $t('common.validation.field.empty')},
                            {value: $v.confirmPassword.passwordConfirmed, msg: $t('common.validation.password.match')}]">
        <password-input-field id="confirm-password"
                              autocomplete="off"
                              :label="$t('common.label.confirmPassword')"
                              @enter-pressed.prevent="onContinue"
                              v-model="$v.confirmPassword.$model"
                              :mandatory="isPasswordMandatory()"
                              :class="{'input-valid': !$v.confirmPassword.$invalid && $v.confirmPassword.passwordConfirmed}"
                              aria-error-element-id="aria-confirm-password-rules-summary confirm-password-errors aria-confirm-password-status"
                              :aria-label-text="$t('common.label.confirmPassword')"
                              :invalid="$v.confirmPassword.$invalid && showErrors">
        </password-input-field>

        <span id="aria-confirm-password-rules-summary" style="height:0; overflow:hidden; position:absolute;" v-if="!confirmPassword">
                    {{ $t('components.password.aria.rules.summary') }}
        </span>
      </validated-field>
      <password-meter
          v-if="showPasswordRegField && !!password && !!isPwdStrengthMeterEnabled"
          :password="password" :first-name="firstname" :last-name="lastname" :user-name="username">
      </password-meter>
      <password-denylist
          v-if="showPasswordRegField && !!password && !!pwdDenyListValidationEnabled"
          :password="password" :first-name="firstname" :last-name="lastname" :user-name="username">
      </password-denylist>
      <password-rules
          v-if="showPasswordRegField && !!password"
          :password="password"
          aria-status-element-id="aria-password-status">
      </password-rules>
      <template>
        <company-registration ref="company"></company-registration>
      </template>
      <template>
        <possible-legal-texts v-if="!!requestedLegalTexts && requestedLegalTexts.hasOwnProperty('checkboxList')" ref="toas"
                              :requested-legal-texts="requestedLegalTexts"
                              :show-errors="showErrors"
                              :country="country"
                              v-on:accepted="checkHideErrors"></possible-legal-texts>
        <possible-user-consents v-if="!!requestedLegalTexts && requestedLegalTexts.hasOwnProperty('consentCategories')" ref="toas"
                              :requested-legal-texts="requestedLegalTexts"
                              :show-errors="showErrors"
                              :country="country"
                              v-on:accepted="checkHideErrors"></possible-user-consents>
      </template>

      <mandatory-label v-if="!!requestedLegalTexts"></mandatory-label>

      <div class="field">
        <button id="continue" type="submit" @click.prevent="onContinue" :disabled="!registrationPossible"
                class="ui fluid primary button" v-text="$tb('pages.registration.create')">
        </button>
      </div>

      <slotted-field :right-slot-method="goToLogin"
                     :right-slot-info-text="$tb('pages.registration.haveAccount')"
                     :right-slot-link-text="$t('pages.login.title')"></slotted-field>

    </div>

  </page-template>
</template>

<script>
import { AsYouType, getCountryCallingCode } from 'libphonenumber-js/mobile'
import Vue from 'vue'
import { helpers, maxLength, required, requiredIf } from 'vuelidate/lib/validators'
import BrandHeader from '../components/BrandHeader.vue'
import DropdownMk2 from '../components/DropdownMk2.vue'
import DropdownSelect from '../components/DropdownSelect.vue'
import LabeledInputField from '../components/LabeledInputField.vue'
import Messages from '../components/Messages.vue'
import MandatoryLabel from '../components/MandatoryLabel.vue'
import PasswordInputField from '../components/LabeledPasswordInputField.vue'
import PasswordDenylist from '../components/PasswordDenylist'
import PasswordMeter from '../components/PasswordMeter'
import PasswordRules from '../components/PasswordRules.vue'
import SlottedField from '../components/SlottedField.vue'
import Titlebar from '../components/Titlebar.vue'
import PossibleLegalTexts from '../components/toas/PossibleLegalTexts'
import PossibleUserConsents from '../components/toas/PossibleUserConsents'
import ValidatedField from '../components/ValidatedField.vue'
import { brand } from '../js/brand.js'
import { mixin } from '../js/ciam-mixin.js'
import { getRecaptchaToken } from '../js/helpers.js'
import { Trans } from '../js/translation.js'
import * as validate from '../js/validation.js'
import { ROUTE_START } from '../router'
import PageTemplate from './template/PageTemplate.vue'
import CompanyRegistration from './CompanyRegistration.vue'

export default {
  components: {
    PossibleLegalTexts,
    PossibleUserConsents,
    PageTemplate,
    Titlebar,
    BrandHeader,
    PasswordRules,
    LabeledInputField,
    Messages,
    MandatoryLabel,
    DropdownSelect,
    DropdownMk2,
    PasswordInputField,
    SlottedField,
    ValidatedField,
    PasswordMeter,
    PasswordDenylist,
    CompanyRegistration
  },
  mixins: [mixin, brand],
  props: {
    formPrefill: {
      type: Object
    }
  },
  data: function () {
    const isUserNameAvailable = this.formPrefill && this.formPrefill.username
    let prefilledUsername = isUserNameAvailable ? this.formPrefill.username : ''
    const isMobile = !!(isUserNameAvailable && this.formPrefill.username.indexOf('@') <= 0)
    let isValidMobile = false

    if (isMobile) {
      const formattedNumber = new AsYouType()
      prefilledUsername = formattedNumber.input(prefilledUsername)
      isValidMobile = formattedNumber.isValid() && !validate.isMobileCountryCodeBlacklisted(prefilledUsername)
    }

    const savedData = this.loadFormData()

    return {
      username: savedData.username || prefilledUsername.replace(/＠/g, '@'),
      firstname: savedData.firstname || '',
      lastname: savedData.lastname || '',
      country: savedData.country || this.getInitialCountry(),
      touchpoint: this.getTouchpointCountry(this.country),
      language: savedData.language || (this.$store.state.lang ? this.$store.state.lang.replace(/-/g, '_') : ''),
      password: '',
      confirmPassword: '',
      mobile: savedData.mobile || '',
      registrationPossible: true,
      requestedLegalTexts: null,
      useMobile: isMobile,
      showErrors: false,
      isValidMobile: isValidMobile,
      nameLength: 64,
      humanCheck: false,
      isBotCheckActive: this.$store.state.isBotCheckActive ? this.$store.state.isBotCheckActive : false,
      isRecaptchaActive: this.$store.state.isRecaptchaActive,
      recaptchaSiteKey: this.$store.state.recaptchaSiteKey,
      isPwdStrengthMeterEnabled: this.$store.state.isPasswordStrengthMeterEnabled ? this.$store.state.isPasswordStrengthMeterEnabled : false,
      pwdDenyListValidationEnabled: this.$store.state.pwdDenyListValidationEnabled ? this.$store.state.pwdDenyListValidationEnabled : false
    }
  },
  validations () {
    return {
      username: this.useMobile ? {
        required,
        validMobile () {
          return this.isValidMobile
        }
      } : {
        required,
        validEmail (input) {
          return !!input && validate.validateEmail(input.trim())
        }
      },
      mobile: {
        requiredIf: requiredIf(() => {
          return this.$store.state.isPasswordAndEmailEnforced && this.$store.state.mobileEnforcedCountries.includes(this.country)
        }),
        validMobile () {
          if (this.$store.state.isPasswordAndEmailEnforced && this.$store.state.mobileEnforcedCountries.includes(this.country)) {
            return this.isValidMobile
          } else {
            return true
          }
        }
      },
      firstname: {
        required,
        maxLength: maxLength(this.nameLength),
        validName (input) {
          return !!input && !this.containsInvalidCharacter(input)
        }
      },
      lastname: {
        required,
        maxLength: maxLength(this.nameLength),
        validName (input) {
          return !!input && !this.containsInvalidCharacter(input)
        }
      },
      country: { required },
      language: { required },
      password: {
        requiredIf: requiredIf(() => {
          return this.isPasswordMandatory()
        }),
        guidelines (input) {
          // if pw is empty we dont need to check the guidelines
          return this.password !== '' ? validate.passwordRulesSatisfied(input) : true
        },
        noInvalidCharacters (input) {
          return !validate.containsInvalidCharacter(input)
        }
      },
      confirmPassword: {
        requiredIf: requiredIf(() => {
          return this.isPasswordMandatory() && this.passwordConfirmationEnabled
        }),
        passwordConfirmed (input) {
          return !helpers.req(input) || input === this.password
        }
      }
    }
  },
  methods: {
    switchType: function () {
      this.useMobile = !this.useMobile

      const countryCode = getCountryCallingCode(this.getInitialCountry())

      this.username = this.useMobile ? '+' + countryCode : ''
      Vue.nextTick(this.focusUsername)
    },
    onContinue: async function () {
      this.saveFormData()
      this.$refs.postman.clear()
      this.showErrors = false
      this.username = this.username?.replace(/＠/g, '@')

      // ensure this.isValidMobile is set properly because when value is loaded from session storage onMobileInput was not executed
      const asYouType = new AsYouType()
      asYouType.input(this.mobile)
      this.isValidMobile = asYouType.isValid() && !validate.isMobileCountryCodeBlacklisted(this.mobile)

      if (!this.isFormValid()) return

      this.$refs.super.startLoading()

      const recaptchaToken = await getRecaptchaToken(this.isRecaptchaActive, this.recaptchaSiteKey, this.$recaptcha.actions.REGISTRATION)

      const payload = this.$api.payloads.buildRegisterPayload(this.firstname, this.lastname, this.country, this.touchpoint, this.language, this.sanitizeUsername(this.username, this.useMobile), this.password, this.sanitizeUsername(this.mobile, this.useMobile), this.$refs.toas.getAcceptedIds(), this.humanCheck, recaptchaToken, this.$refs.company.getCompanyFields())
      this.$api.register.registerUser(payload, this.onRegistrationSuccess, this.onFail)
    },
    isFormValid: function () {
      if (!this.$v.$anyDirty || this.$v.$anyError || this.$v.$invalid || this.$refs.toas.$v.$invalid || !this.$refs.company.companyFormValid()) {
        const _this = this

        this.focusFirstErrorField(function () {
          _this.showErrors = true
          _this.$refs.company.showErrors = true
        })

        return false
      }

      return true
    },
    onRegistrationSuccess: function ({ uid, codeSent }) {
      console.debug(`Created user, routing to confirmation page [uid=${uid}]`)

      sessionStorage.removeItem('registrationFormData')

      const storeUpdate = {
        uid: uid,
        lang: this.language,
        country: this.country,
        username: this.username
      }

      this.updateStore(storeUpdate)

      sessionStorage.setItem('storedrid', this.$store.state.rid)
      sessionStorage.setItem('storedusername', this.username)

      this.$router.push({
        name: 'GOTO_CONFIRM_REG_USERNAME',
        params: {
          codeSent: codeSent
        }
      })
    },
    onFail: function (resp) {
      this.onAjaxError(resp)
      this.$refs.super.stopLoading()
      this.$refs.username.focus()
    },
    fetchLegalTexts: function () {
      if (!this.country || !this.language) return

      this.$refs.super.startLoading()
      this.$refs.postman.clear()

      this.requestedLegalTexts = null
      this.registrationPossible = false
      const touchpoint = this.getTouchpointCountry(this.country)

      console.debug(`Fetching legal texts [homeCountry=${this.country}, touchpoint=${touchpoint}, language=${this.language}]`)

      const payload = this.$api.payloads.buildLegalTextsPayload(this.country, touchpoint, this.language)
      this.$api.toas.fetchRegistrationTexts(payload, this.setupLegalTexts, this.onToasFetchError)

      if (this.$store.state.isPasswordAndEmailEnforced && this.$store.state.mobileEnforcedCountries.includes(this.country)) {
        const countryCode = getCountryCallingCode(this.getInitialCountry())
        if (!this.mobile) {
          this.mobile = '+' + countryCode
        }
        $('#mobile').parent().parent().css('display', 'block')
      } else {
        if (!this.mobile) {
          this.mobile = ''
        }
        $('#mobile').parent().parent().css('display', 'none')
      }
    },
    setupLegalTexts: function (resp) {
      this.requestedLegalTexts = resp
      this.registrationPossible = true
      if (this.$refs.super) {
        this.$refs.super.stopLoading()
      }
    },
    onToasFetchError: function () {
      console.error('Failed to fetch legal texts')
      $('.ui.form').form('add errors', [this.$t('errors.registration.loadLegalTexts')])
      this.$refs.super.stopLoading()
    },
    preselectLanguage: function () {
      const keys = Object.keys(this.languageOptions)
      if (keys.length > 0) {
        this.language = keys[0]
        this.updateLanguage()
        this.$nextTick(() => {
          if (this.$refs.languageDropdown && this.$refs.languageDropdown.resetIntervalValue) {
            this.$refs.languageDropdown.resetIntervalValue()
          }
        })
      }
    },
    updateCountry: function () {
      this.preselectLanguage()
      this.fetchLegalTexts()
    },
    updateLanguage: function () {
      if (!this.language) return

      const langUpdate = {
        lang: this.language
      }

      this.updateStore(langUpdate)

      if (this.$refs.company) {
        this.$refs.company.fetchCompanyProfileFieldsData()
      }

      Trans.loadAndSetLanguageFile(this.language, this.$i18n, function () {
        Vue.nextTick(function () {
          // Transfer variables necessary since there is no "this" context in this method
          jQuery('#brandDesc').popup('refresh')
        })
      })
    },
    goToLogin: function () {
      this.$router.push(
        {
          name: ROUTE_START,
          params: {
            usernameFromRegistration: this.username
          }
        }
      )
    },
    onMobileInput: function () {
      if (this.$store.state.isPasswordAndEmailEnforced && this.mobile) {
        const asYouType = new AsYouType()
        this.mobile = asYouType.input(this.mobile.replace(/[^0-9+ ]/g, ''))
        this.isValidMobile = asYouType.isValid() && !validate.isMobileCountryCodeBlacklisted(this.mobile)
        $('#mobile').val(this.mobile)
      } else if (this.$store.state.isPasswordAndEmailEnforced) {
        this.isValidMobile = false
      } else {
        if (validate.validateEmail(this.username.trim())) return
        if (this.username) {
          const asYouType = new AsYouType()
          this.username = asYouType.input(this.username.replace(/[^0-9+ ]/g, ''))
          this.isValidMobile = asYouType.isValid() && !validate.isMobileCountryCodeBlacklisted(this.username)
          $('#username').val(this.username)
        } else {
          this.isValidMobile = false
        }
      }
    },
    checkHideErrors: function () {
      if (this.$v.$invalid) return
      if (this.$refs.toas && this.$refs.toas.$v.$invalid) return

      this.showErrors = false
      $('form .ui.error.message').empty() // clear ajax errors
    },
    containsInvalidCharacter: function (name) {
      return validate.nameContainsInvalidCharacter(name)
    },
    init: function () {
      if (this.isBotCheckActive) {
        $("input[name='registrationCheck']").hide()
      }

      this.$refs.username.focus()
    },
    saveFormData: function () {
      const formData = {
        username: this.username,
        firstname: this.firstname,
        lastname: this.lastname,
        country: this.country,
        language: this.language,
        mobile: this.mobile
      }
      sessionStorage.setItem('registrationFormData', JSON.stringify(formData))
    },
    loadFormData: function () {
      const savedData = sessionStorage.getItem('registrationFormData')
      return savedData ? JSON.parse(savedData) : {}
    }
  },
  computed: {
    countryOptions: function () {
      return this.$tCountries(true)
    },
    languageOptions: function () {
      return this.$tLanguages(this.country)
    },
    usernameEmptyError: function () {
      return this.useMobile ? this.$t('common.validation.username.mobileEmpty') : this.$t('common.validation.username.emailEmpty')
    },
    passwordAndEmailEnforced: function () {
      return this.$store.state.isPasswordAndEmailEnforced
    },
    passwordConfirmationEnabled: function () {
      return this.$store.state.isPasswordConfirmationEnabled
    }
  },
  mounted: function () {
    this.init()
    console.info(`Registration [formPrefill=${JSON.stringify(this.formPrefill)}, language=${this.language}, mfa=${this.isMFA()}]`)

    if (this.$refs.languageDropdown) {
      this.$refs.languageDropdown.$refs.options.style.height = 'auto'
    }
    window.addEventListener('beforeunload', this.saveFormData)

    // ensure language is loaded and displayed properly when value is loaded from session storage
    const savedData = this.loadFormData()
    if (savedData.language) {
      this.language = savedData.language
      this.updateLanguage()
    }
  },
  destroyed: function () {
    window.removeEventListener('beforeunload', this.saveFormData)
  },
  watch: {
    username: {
      handler (newVal, oldVal) {
        this.saveFormData()
        const formattedNumber = new AsYouType()
        newVal = newVal.replace(/＠/g, '@')
        formattedNumber.input(newVal)

        if (!this.$store.state.isPasswordAndEmailEnforced && formattedNumber.isValid() && !this.useMobile && !validate.isMobileCountryCodeBlacklisted(newVal)) {
          this.useMobile = true
          this.$v.username.$model = newVal
          this.isValidMobile = true
        }

        if (validate.validateEmail(newVal.trim()) && this.useMobile) {
          this.useMobile = false
          this.$v.username.$model = newVal
          this.isValidMobile = false
        }
      }
    },
    password: {
      handler (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.showErrors = false
          $('#grp_password').siblings('.ui.basic.red').remove()
        }
      }
    },
    $v: {
      handler () {
        this.checkHideErrors()
      },
      deep: true
    },
    firstname: {
      handler () {
        this.saveFormData()
      }
    },
    lastname: {
      handler () {
        this.saveFormData()
      }
    },
    country: {
      handler () {
        this.saveFormData()
      }
    },
    language: {
      handler () {
        this.saveFormData()
      }
    },
    mobile: {
      handler () {
        this.saveFormData()
      }
    },
    langConfigLoaded: {
      handler () {
        this.preselectLanguage()
        this.fetchLegalTexts()
      }
    }
  }
}
</script>
<style>
.ui.divider {
  margin: 0.8rem 0;
}

.input-valid {
  border: 2px solid green;
  background-color: #ceffce
}

@media screen and (max-width: 768px) {
  #language.ui.selection.dropdown,
  #language.ui.dropdown.selection,
  #country.ui.selection.dropdown,
  #country.ui.dropdown.selection {
    line-height: 1.4 !important;
    padding-top: 0.65em !important;
    padding-bottom: 0.4em !important;
    min-height: 2.5em !important;
    overflow: visible !important;
  }

  #language .text,
  #country .text {
    line-height: 1.4 !important;
    padding-bottom: 0.1em !important;
    vertical-align: middle !important;
  }
}
</style>
